import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const antralFollicleCount: ContentBlock<"procedure"> = {
  id: makeId("proc-antral-follicle-count"),
  kind: "procedure",
  tags: ["testing", "afc", "her", "clinic-visit"],
  title: "Antral follicle count",
  subtitle:
    "Ultrasound to count small follicles as a marker of ovarian reserve",
  body: {
    procedureName: "Antral follicle count",
    category: "diagnostic",
    description:
      "A transvaginal ultrasound performed early in your cycle to count the small resting follicles (2–10 mm) visible on each ovary. The antral follicle count (AFC) is one of the best indicators of ovarian reserve and helps predict response to stimulation medications.",
    howItWorks:
      "The exam is a standard transvaginal ultrasound performed on cycle day 2–5, when follicles are at their baseline resting size. The sonographer carefully scans each ovary, counting all visible follicles between 2 and 10 mm in diameter. The total count across both ovaries gives your AFC. A higher count generally suggests a larger egg supply, while a lower count may indicate diminished ovarian reserve. The AFC is often combined with AMH blood levels for a complete picture.",
    duration: "10–15 minutes",
    anesthesia: "none",
    whatToExpect: [
      "A transvaginal ultrasound, with mild pressure but not painful",
      "Best performed on cycle day 2–5 for the most accurate count",
      "Your follicle count will usually be shared with you during the exam",
      "The result helps your doctor choose the right stimulation protocol and medication dosage",
    ],
    results: [
      {
        label: "When you hear",
        detail:
          "The follicle count is often available during the ultrasound because the follicles are counted in real time.",
      },
      {
        label: "What you learn",
        detail:
          "The total count across both ovaries helps estimate ovarian reserve and likely response to stimulation medication.",
      },
      {
        label: "How it is used",
        detail:
          "Your doctor usually combines AFC with AMH, age, and prior response before choosing a protocol or dose.",
      },
    ],
    prepInstructions: [
      "Schedule the appointment for cycle day 2–5 (early in your period)",
      "Empty your bladder before the exam",
      "No fasting or special preparation required",
    ],
    risks: [
      "No known risks: standard transvaginal ultrasound",
      "Mild temporary discomfort from the probe",
    ],
    forWhom: "her",
    frBody: {
      procedureName: "Compte des follicules antraux",
      description:
        "Échographie endovaginale réalisée en début de cycle pour compter les petits follicules au repos (2–10 mm) visibles sur chaque ovaire. Le compte des follicules antraux (CFA) est l'un des meilleurs indicateurs de la réserve ovarienne et aide à prédire la réponse aux médicaments de stimulation.",
      howItWorks:
        "L'examen est une échographie endovaginale standard réalisée entre le 2e et le 5e jour du cycle, lorsque les follicules sont à leur taille de repos. L'échographiste examine soigneusement chaque ovaire et compte tous les follicules visibles entre 2 et 10 mm de diamètre. La somme des deux ovaires donne votre CFA. Un compte plus élevé suggère généralement une réserve ovocytaire plus importante, tandis qu'un compte faible peut évoquer une réserve diminuée. Le CFA est souvent associé au dosage sanguin d'AMH pour obtenir un tableau complet.",
      duration: "10 à 15 minutes",
      whatToExpect: [
        "Échographie endovaginale, avec une légère pression mais sans douleur",
        "À réaliser entre le 2e et le 5e jour du cycle pour un compte plus fiable",
        "Le nombre de follicules vous est généralement communiqué pendant l'examen",
        "Le résultat aide votre médecin à choisir le protocole et la posologie de stimulation",
      ],
      results: [
        {
          label: "Quand vous avez des nouvelles",
          detail:
            "Le compte folliculaire est souvent disponible pendant l'échographie, car les follicules sont comptés en temps réel.",
        },
        {
          label: "Ce que vous apprenez",
          detail:
            "Le total des deux ovaires aide à estimer la réserve ovarienne et la réponse probable aux médicaments de stimulation.",
        },
        {
          label: "Comment c'est utilisé",
          detail:
            "Votre médecin associe généralement le CFA à l'AMH, l'âge et les réponses antérieures avant de choisir un protocole ou une dose.",
        },
      ],
      prepInstructions: [
        "Prenez rendez-vous entre le 2e et le 5e jour du cycle (début des règles)",
        "Videz votre vessie avant l'examen",
        "Aucun jeûne ni préparation particulière nécessaire",
      ],
      risks: [
        "Aucun risque connu : échographie endovaginale standard",
        "Léger inconfort transitoire lié à la sonde",
      ],
    },
  },
  sources: [
    {
      title:
        "Testing and interpreting measures of ovarian reserve: a committee opinion",
      authors: "Practice Committee of the ASRM",
      journal: "Fertility and Sterility",
      year: 2020,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/testing-and-interpreting-measures-of-ovarian-reserve-a-committee-opinion-2020/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(antralFollicleCount);

registerRelationship({
  from: makeId("proc-antral-follicle-count"),
  to: makeId("dp-follicle-count"),
  type: "produces",
});

registerRelationship({
  from: makeId("proc-antral-follicle-count"),
  to: makeId("test-afc"),
  type: "related-to",
  label: "Canonical test",
});
